import React, { useState, useMemo } from "react";
import { Button, Sheet, Attention } from "@optimizely/axiom";
import { datadogRum } from "@datadog/browser-rum";
import { ISSOSamlFormFields } from "../SSOSamlForm/SSOSamlFormFields/SSOSamlFormFields";
import { ISAMLIdp, SAMLIdpUpdateFields } from "../../../../domain/IdentityProvider";
import { ConnectionNameInput } from "../ConnectionNameInput/ConnectionNameInput";
import { IssuerURLField } from "../SSOOIDCForm/SSOOIDCFormFields/IssuerURLField";
import { SSOURLField } from "../SSOOIDCForm/SSOOIDCFormFields/SSOURLField";
import { emitToast } from "../../../../lib/toaster-utils";
import { UNEXPECTED_ERROR } from "../../../../constants/constants";

export const SSOSamlEditSheet = ({
    idp,
    onClose,
    onSave
}: {
    idp: ISAMLIdp;
    onClose: () => void;
    onSave: (fields: SAMLIdpUpdateFields) => Promise<void>;
}) => {
    const initialValues = useMemo(
        () => ({
            idpName: idp.idpName || "",
            issuerUrl: idp.issuerUrl ?? "",
            ssoUrl: idp.ssoUrl ?? "",
            signatureCertificate: null
        }),
        [idp]
    );

    const [fields, setFields] = useState<ISSOSamlFormFields>(initialValues);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [validationError, setValidationError] = useState(false);

    const updateValues = (updatedFields: Partial<ISSOSamlFormFields>, hasError?: boolean) => {
        if (hasError) {
            setValidationError(true);
        } else {
            setValidationError(false);
        }
        setFields((prevFields) => ({ ...prevFields, ...updatedFields }));
    };

    const incompleteSAMLForm = () => {
        const requiredValues = ["idpName", "issuerUrl", "ssoUrl"];
        return !requiredValues.every((field) => !!fields[field as keyof ISSOSamlFormFields]);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError("");
        try {
            await onSave({
                id: idp.id,
                idpName: fields.idpName,
                issuerUrl: fields.issuerUrl,
                ssoUrl: fields.ssoUrl
            });
            onClose();
            emitToast({
                message: "SAML Connection updated successfully."
            });
        } catch (e) {
            if (Array.isArray(e)) {
                const [error] = e;
                datadogRum.addError(error);
                setError(error?.message ?? UNEXPECTED_ERROR);
            } else {
                datadogRum.addError(e);
                console.error(e);
                setError(UNEXPECTED_ERROR);
            }
        } finally {
            setIsSaving(false);
        }
    };

    // Check if any values have changed from initial values
    const hasChanges = useMemo(() => {
        return (
            fields.idpName !== initialValues.idpName ||
            fields.issuerUrl !== initialValues.issuerUrl ||
            fields.ssoUrl !== initialValues.ssoUrl
        );
    }, [fields, initialValues]);

    return (
        <Sheet
            onClose={onClose}
            title="Edit SAML Connection"
            footerButtonList={[
                <Button key="close" onClick={onClose} isDisabled={isSaving} data-testid="saml-edit-cancel-button">
                    Cancel
                </Button>,
                <Button
                    key="save"
                    // eslint-disable-next-line react/style-prop-object
                    style="highlight"
                    onClick={() => handleSave()}
                    isDisabled={isSaving || !hasChanges || incompleteSAMLForm() || validationError}
                    isLoading={isSaving}
                    aria-busy={isSaving ? "true" : "false"}
                    data-testid="saml-edit-save-button"
                >
                    Save
                </Button>
            ]}
        >
            {error && <Attention type="bad-news">{error}</Attention>}
            <ConnectionNameInput onChange={(value) => updateValues({ idpName: value })} value={fields.idpName} />
            <IssuerURLField
                required
                onChange={(value) => updateValues({ issuerUrl: value })}
                value={fields.issuerUrl}
            />
            <SSOURLField
                onChange={(value, hasError) => updateValues({ ssoUrl: value }, hasError)}
                required
                value={fields.ssoUrl}
            />
        </Sheet>
    );
};

SSOSamlEditSheet.displayName = "SSOSamlEditSheet";
